import { useState, useRef, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";

export const InteractiveTerminal = ({
  labId,
  environment = "docker",
  externalSessionId = null,   // si fourni, le parent gère le cycle de vie de la session
  externalMode = null,
  fill = false,               // remplir la hauteur du conteneur parent
}) => {
  const { token } = useAuth();
  const [history, setHistory] = useState([]);
  const [currentInput, setCurrentInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const cmdHistoryRef = useRef([]);   // commandes tapées (nav ↑/↓)
  const histIdxRef = useRef(-1);
  const [session, setSession] = useState(
    externalSessionId ? { session_id: externalSessionId, mode: externalMode } : null
  );
  const [starting, setStarting] = useState(!externalSessionId);
  const inputRef = useRef(null);
  const terminalRef = useRef(null);
  const sessionRef = useRef(null);

  // Session pilotée par le parent : pas de start/end ici
  useEffect(() => {
    if (!externalSessionId) return;
    setSession({ session_id: externalSessionId, mode: externalMode });
    setStarting(false);
    const modeLabel = externalMode === "docker" || externalMode === "ssh" ? "Environnement réel" : externalMode === "external" ? "Service externe" : "Simulation";
    setHistory([
      { type: "system", content: `DevOps Learn Terminal — ${environment.toUpperCase()} [${modeLabel}]` },
      { type: "system", content: "Environnement prêt. Tapez vos commandes ci-dessous." },
      { type: "system", content: "─".repeat(50) },
    ]);
  }, [externalSessionId, externalMode, environment]);

  // Démarre une vraie session de lab (conteneur réel si configuré) — mode autonome
  useEffect(() => {
    if (externalSessionId) return; // géré par le parent
    let cancelled = false;
    const startSession = async () => {
      setStarting(true);
      setHistory([
        { type: "system", content: `DevOps Learn Terminal — ${environment.toUpperCase()}` },
        { type: "system", content: "Démarrage de l'environnement..." },
      ]);
      try {
        const res = await axios.post(
          `${API}/labs/sessions/start`,
          { lab_id: labId, environment },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const data = res.data;
        setSession(data);
        sessionRef.current = data.session_id;
        const modeLabel =
          data.mode === "docker" || data.mode === "ssh"
            ? "Environnement réel"
            : data.mode === "external"
            ? "Service externe"
            : "Simulation";
        setHistory([
          { type: "system", content: `DevOps Learn Terminal — ${environment.toUpperCase()} [${modeLabel}]` },
          { type: "system", content: data.message || "Session démarrée." },
          { type: "system", content: "─".repeat(50) },
        ]);
      } catch (error) {
        if (cancelled) return;
        setHistory((prev) => [
          ...prev,
          { type: "error", content: "Impossible de démarrer l'environnement. Réessayez plus tard." },
        ]);
      } finally {
        if (!cancelled) setStarting(false);
      }
    };
    if (token) startSession();

    return () => {
      cancelled = true;
      // Nettoyage : fermer la session (best-effort)
      if (sessionRef.current) {
        axios
          .post(
            `${API}/labs/sessions/${sessionRef.current}/end`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          )
          .catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labId, environment]);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  const executeCommand = async (command) => {
    if (!command.trim()) {
      setHistory((prev) => [...prev, { type: "input", content: "$ " }]);
      return;
    }
    setHistory((prev) => [...prev, { type: "input", content: `$ ${command}` }]);
    cmdHistoryRef.current.push(command);
    histIdxRef.current = -1;

    if (command.trim() === "clear") {
      setHistory([]);
      return;
    }
    if (!session?.session_id) {
      setHistory((prev) => [...prev, { type: "error", content: "Session non prête." }]);
      return;
    }

    setIsProcessing(true);
    try {
      const response = await axios.post(
        `${API}/labs/sessions/${session.session_id}/execute`,
        { session_id: session.session_id, command },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { output, exit_code } = response.data;
      setHistory((prev) => [
        ...prev,
        { type: exit_code === 0 ? "output" : "error", content: output || "(no output)" },
      ]);
    } catch (error) {
      setHistory((prev) => [...prev, { type: "error", content: "Erreur lors de l'exécution de la commande" }]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Autocomplétion (Tab) : interroge l'environnement pour les candidats
  const handleTab = async () => {
    if (!session?.session_id || !currentInput.trim()) return;
    try {
      const res = await axios.post(
        `${API}/labs/sessions/${session.session_id}/complete`,
        { input: currentInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const cands = res.data.candidates || [];
      if (cands.length === 0) return;
      const tokens = currentInput.split(" ");
      const last = tokens[tokens.length - 1];
      // préfixe commun le plus long
      let common = cands[0];
      for (const c of cands) {
        while (!c.startsWith(common)) common = common.slice(0, -1);
      }
      if (cands.length === 1 || common.length > last.length) {
        tokens[tokens.length - 1] = cands.length === 1 ? cands[0].replace(/\/$/, "") : common;
        setCurrentInput(tokens.join(" "));
      }
      if (cands.length > 1) {
        setHistory((prev) => [
          ...prev,
          { type: "input", content: `$ ${currentInput}` },
          { type: "system", content: cands.join("   ") },
        ]);
      }
    } catch {
      /* silencieux */
    }
  };

  const handleKeyDown = (e) => {
    const input = inputRef.current;
    if (e.key === "Enter" && !isProcessing) {
      executeCommand(currentInput);
      setCurrentInput("");
    } else if (e.key === "Tab") {
      e.preventDefault();
      handleTab();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const h = cmdHistoryRef.current;
      if (h.length) {
        histIdxRef.current = histIdxRef.current < 0 ? h.length - 1 : Math.max(0, histIdxRef.current - 1);
        setCurrentInput(h[histIdxRef.current]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const h = cmdHistoryRef.current;
      if (histIdxRef.current >= 0 && histIdxRef.current < h.length - 1) {
        histIdxRef.current += 1;
        setCurrentInput(h[histIdxRef.current]);
      } else {
        histIdxRef.current = -1;
        setCurrentInput("");
      }
    } else if (e.ctrlKey && e.key === "l") {
      e.preventDefault();
      setHistory([]);
    } else if (e.ctrlKey && e.key === "u") {
      e.preventDefault();
      setCurrentInput("");
    } else if (e.ctrlKey && e.key === "c") {
      e.preventDefault();
      setHistory((prev) => [...prev, { type: "input", content: `$ ${currentInput}^C` }]);
      setCurrentInput("");
    } else if (e.ctrlKey && e.key === "a") {
      e.preventDefault();
      input?.setSelectionRange(0, 0);
    } else if (e.ctrlKey && e.key === "e") {
      e.preventDefault();
      const len = currentInput.length;
      input?.setSelectionRange(len, len);
    }
  };

  const focusInput = () => inputRef.current?.focus();

  return (
    <div
      className={`terminal rounded-lg overflow-hidden ${fill ? "h-full flex flex-col" : ""}`}
      onClick={focusInput}
      data-testid="interactive-terminal"
    >
      {/* Terminal Header */}
      <div className="bg-[#1a1a1a] px-4 py-2 flex items-center gap-2 border-b border-[#333] shrink-0">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
          <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
          <div className="w-3 h-3 rounded-full bg-[#27ca3f]"></div>
        </div>
        <span className="text-xs text-gray-400 ml-2 font-mono">devops@learn:~/{environment}</span>
        {session?.mode && (
          <span className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded bg-[#222] text-gray-400">
            {session.mode === "docker" || session.mode === "ssh" ? "● environnement réel" : session.mode}
          </span>
        )}
      </div>

      {/* Terminal Body */}
      <div ref={terminalRef} className={`p-4 overflow-y-auto font-mono text-sm ${fill ? "flex-1 min-h-0" : "h-80"}`} style={{ background: "#0a0a0a" }}>
        {history.map((item, index) => (
          <div
            key={index}
            className={`mb-1 ${
              item.type === "input"
                ? "text-cyan-400"
                : item.type === "error"
                ? "text-red-400"
                : item.type === "system"
                ? "text-yellow-400"
                : "text-green-400"
            }`}
          >
            <pre className="whitespace-pre-wrap">{item.content}</pre>
          </div>
        ))}

        {/* Current Input Line */}
        <div className="flex items-center text-cyan-400">
          <span className="text-gray-500">$</span>
          <input
            ref={inputRef}
            type="text"
            value={currentInput}
            onChange={(e) => setCurrentInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 ml-2 bg-transparent outline-none terminal-input"
            disabled={isProcessing || starting}
            autoFocus
            data-testid="terminal-input"
          />
          {starting ? (
            <span className="text-yellow-400 animate-pulse">démarrage...</span>
          ) : isProcessing ? (
            <span className="text-yellow-400 animate-pulse">processing...</span>
          ) : (
            <span className="w-2 h-4 bg-green-400 cursor-blink ml-0.5"></span>
          )}
        </div>
      </div>
    </div>
  );
};

export default InteractiveTerminal;
